/**
 * Centralized Web Audio API utilities
 *
 * This module provides audio playback functions for notifications and UI feedback.
 * Consolidated from useNotifications.ts and useBadgePulse.ts to reduce code duplication.
 */

import { logger } from "@/lib/logger";
interface WindowWithWebkit extends Window {
  webkitAudioContext?: typeof AudioContext;
}

interface PlaySoundOptions {
  frequency: number;
  secondFrequency?: number;
  duration: number;
  gain: number;
}

/**
 * Generic function to play a sound using Web Audio API
 */
export const playSound = (options: PlaySoundOptions): void => {
  const { frequency, secondFrequency, duration, gain } = options;

  try {
    const audioContext = new (
      window.AudioContext || (window as WindowWithWebkit).webkitAudioContext!
    )();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Set frequencies
    oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
    if (secondFrequency) {
      oscillator.frequency.setValueAtTime(secondFrequency, audioContext.currentTime + duration / 3);
    }

    // Set gain envelope
    gainNode.gain.setValueAtTime(gain, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

    oscillator.type = "sine";
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration);
  } catch (e) {
    logger.debug("Could not play sound:", e);
  }
};

/**
 * Play the main notification sound (two-tone, pleasant notification)
 * Used when a new notification arrives
 */
export const playNotificationSound = (): void => {
  playSound({
    frequency: 880, // A5 note
    secondFrequency: 1108.73, // C#6 note
    duration: 0.3,
    gain: 0.1,
  });
};

/**
 * Play a subtle badge sound (single short tone)
 * Used when sidebar badge count increases
 */
export const playBadgeSound = (): void => {
  playSound({
    frequency: 660, // E5 note
    duration: 0.15,
    gain: 0.08,
  });
};
